import { Dispatch, useContext, useState } from 'react';
import { path, init, isEmpty, toUpper, update, equals } from 'ramda';
import {
  BusinessDataFormContext,
  IBusinessDataFormContext,
} from '../Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { attributeDefToDataPath } from '../../../../Utils/dataUtils';
import { businessDataFormValuesEqual } from '../Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataFormHasChanged/businessDataFormHasChanged';
import Logger from '../../../../Utils/Logger';
import {
  AvenyCollectionMap,
  ModelContext,
} from '../../../../Model/ModelContext/ModelContext';
import {
  safeGetAttributeInfo,
  getValidClassesList,
} from '../../../../Model/avenyModelUtils';
import {
  IClientAttributeModel,
  IClientBusinessModel,
} from '../../../../Types/ClientModelTypes';
import { INodeData } from '../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import {
  BusinessDataFormAction,
  IFieldValidations,
} from '../Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';

export interface IUseAttributeValuesResult {
  value: string | undefined;
  /** Om värdet är ändrat på */
  valueEdited: boolean;
  /** Om attributet är valid enligt modellens ValidInComps */
  isValid: boolean | undefined;
  model: IClientBusinessModel | undefined;
  attribute: IClientAttributeModel | undefined;
  attributeResultError: string | undefined;
  validations: IFieldValidations;
  editMode: boolean;
  formStateDispatch?: Dispatch<BusinessDataFormAction>;
  data?: INodeData;
  editCancelled: boolean;
  touched: boolean;
  dataLoading: boolean;
  savedValue: string | undefined;
  /** Om attributets värde är dolt (maskerat) för användaren */
  masked: boolean;
}

export const MASKED_VALUE_TEXT = 'Dold uppgift';
export const MASKED_FIELD_NAME = '_maskvalues';

export const getMaskedFlagFromCurrentValues = (
  currentValues: INodeData | undefined,
  attributePath: string
): boolean => {
  const dataPath = attributeDefToDataPath(attributePath);
  const parentPath = init(dataPath);
  return parentPath.length > 0
    ? path([...parentPath, MASKED_FIELD_NAME], currentValues) === true
    : currentValues?.[MASKED_FIELD_NAME] === true;
};

export const getMasked = (
  attribute: IClientAttributeModel | undefined,
  maskedFlag: boolean
): boolean => {
  return Boolean(attribute?.MaskedValue && maskedFlag);
};

export const getMaskedFromCurrentValues = (
  currentValues: INodeData | undefined,
  attributePath: string,
  attribute: IClientAttributeModel | undefined
): boolean => {
  const maskedFlag = getMaskedFlagFromCurrentValues(
    currentValues,
    attributePath
  );
  const masked = getMasked(attribute, maskedFlag);
  return masked;
};

/**
 *
 * @param avenyCollectionMap
 * @param formContext
 * @param attributePath
 */
export const getAttributeValues = (
  avenyCollectionMap: AvenyCollectionMap,
  formContext: IBusinessDataFormContext<INodeData>,
  attributePath: string
): IUseAttributeValuesResult => {
  if (formContext) {
    const dataPath = attributeDefToDataPath(attributePath);

    const dataValue =
      formContext.data && (path(dataPath, formContext.data) as string);

    const value = path(dataPath, formContext.formState.currentValues) as string;

    const valueEdited =
      value !== undefined && !businessDataFormValuesEqual(dataValue, value);

    //validering
    const validations = formContext.formState.validation.filter(validation =>
      typeof validation === 'object'
        ? validation.fieldName === attributePath &&
          (!validation.path || validation.path.length === 0)
        : false
    );

    //samma uppslag av värde, fast för _typ
    const typ = path(
      attributeDefToDataPath(
        update(-1, '_typ', attributePath.split('.')).join('.')
      ),
      formContext.formState.currentValues
    ) as string;

    const attributeResult = safeGetAttributeInfo(
      formContext.model.CollectionName,
      attributePath,
      avenyCollectionMap
    );

    let isValid: boolean | undefined;
    let attribute: IClientAttributeModel | undefined;
    let attributeResultError: string | undefined;
    if (attributeResult.isOk()) {
      attribute = attributeResult.value.attribute;
      const ValidInComps = attribute.ValidInComps ?? [];
      const validTypes = ValidInComps.flatMap(validIn =>
        getValidClassesList([formContext.model.RootType], validIn)
      );

      isValid =
        typ === undefined ||
        isEmpty(validTypes) ||
        validTypes.map(toUpper).includes(typ);
    } else {
      attributeResultError = attributeResult.error;
    }

    const touched = Boolean(path(dataPath, formContext.formState.touched));

    const maskedFlag = getMaskedFlagFromCurrentValues(
      formContext.formState.currentValues,
      attributePath
    );
    const masked = getMasked(attribute, maskedFlag);

    return {
      value,
      valueEdited,
      isValid,
      attribute,
      attributeResultError,
      validations,
      editMode: formContext.formState.editMode,
      model: formContext.model,
      formStateDispatch: formContext.formStateDispatch,
      data: formContext.data,
      editCancelled: formContext.formState.editCancelled,
      touched,
      dataLoading: Boolean(formContext.formState.loading),
      savedValue: dataValue,
      masked,
    };
  } else {
    Logger.error(
      new Error('Attributvärde kan inte hämtas utanför BusinessDataFormContext')
    );
    return {
      value: undefined,
      valueEdited: false,
      isValid: false,
      attribute: undefined,
      attributeResultError: undefined,
      validations: [],
      editMode: false,
      model: undefined,
      formStateDispatch: undefined,
      editCancelled: false,
      touched: false,
      dataLoading: false,
      savedValue: undefined,
      masked: false,
    };
  }
};

const isDifferentAttributeValues = (
  values: IUseAttributeValuesResult,
  newValues: IUseAttributeValuesResult
) => {
  return (
    values.value !== newValues.value ||
    values.valueEdited !== newValues.valueEdited ||
    values.isValid !== newValues.isValid ||
    values.attribute !== newValues.attribute ||
    values.attributeResultError !== newValues.attributeResultError ||
    !equals(values.validations, newValues.validations) ||
    values.editMode !== newValues.editMode ||
    values.model !== newValues.model ||
    values.formStateDispatch !== newValues.formStateDispatch ||
    values.editCancelled !== newValues.editCancelled ||
    values.touched !== newValues.touched ||
    values.dataLoading !== newValues.dataLoading ||
    values.savedValue !== newValues.savedValue ||
    values.masked !== newValues.masked
  );
};
/**
 *
 * @param attributePath
 */
export const useAttributeValues = (
  attributePath: string
): IUseAttributeValuesResult => {
  const formContext = useContext<IBusinessDataFormContext>(
    BusinessDataFormContext
  );
  const { avenyCollectionMap } = useContext(ModelContext);
  const [values, setValues] = useState(() =>
    getAttributeValues(avenyCollectionMap, formContext, attributePath)
  );

  const newValues = getAttributeValues(
    avenyCollectionMap,
    formContext,
    attributePath
  );
  if (isDifferentAttributeValues(values, newValues)) {
    setValues(newValues);
    return newValues;
  }
  return values;
};
